import React, { useEffect, useMemo, useState } from 'react';
import { isBlank } from 'underscore.string';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useRouter } from 'next/compat/router';
import { removeTypename } from 'shared/helpers/utils';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';
import { useObserver } from 'mobx-react-lite';
import useDelayedRender from 'shared/hooks/use-delayed-render';

import { MainContent } from 'components/skip-to-main';
import { useModals, ModalNames } from 'components/modals';
import useUser from 'hooks/use-user';
import { getPersistedValue } from 'shared/utils/persisted-values';

import useUI from 'hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { getHtmlPlainText } from 'shared/utils/misc-utils';
import useDispensaryCategories from 'src/dispensary/hooks/use-dispensary-categories';
import { useHideCategoryTiles } from 'shared/hooks/use-hide-category-tiles';
import { AmplitudeEvents, useAmplitude } from 'shared/hooks/use-amplitude';
import useCart from 'shared/hooks/use-cart';
import { useRemoteComponent, RemoteComponentSlot } from 'hooks/use-remote-component';

import { MenuImageBanner } from 'src/components/menu-image-banner';
import { PersonalizedProducts } from 'components/carousels/products-carousels/personalized-products-carousel';
import { BuyItAgainCarousel } from 'components/carousels/products-carousels/buy-it-again-carousel';
import { HomePageSponsoredBrandCarousel } from 'src/components/carousels/ads-carousels/home-page-sponsored-brand-carousel';
import { CategoriesNav } from 'src/components/categories-nav';
import { useAdsEnabled } from 'src/utils/ads/hooks/use-ads-enabled';
import { DutchieLoyaltyWidget } from 'src/components/widgets/dutchie-loyalty-widget';
import { useLoyalty } from 'src/components/widgets/loyalty-tiers/use-loyalty';
import { isReferralsRoute as isReferralsRouteUtils } from 'src/components/loyalty/rewards/dpos-utils';
import { LoginCta } from 'src/components/ctas';

import { useProductsForHomePage } from 'src/dispensary/hooks/use-products-for-home-page';
import { useShowBuyItAgainCarousel } from 'src/dispensary/hooks/use-show-buy-it-again-carousel';
import { HomepageCarouselSections } from 'components/homepage-carousel-sections';

import useErnie from 'shared/hooks/use-ernie';
import { ERNIE_TYPES } from 'shared/constants';
import MenuBanner from './components/menu-banner';
import { PlaceholderMenuImageBanner, PlaceholderCategoriesNav } from './components/placeholder-content';

// Delay showing placeholders to avoid flash when data loads quickly
const PLACEHOLDER_DELAY_MS = 200;

export default function CoreMenuDispensaryPage(): JSX.Element | null {
  const UI = useUI();
  const { dispensary } = useDispensary();
  const { categories } = useDispensaryCategories();
  const hideCategoryTiles = useHideCategoryTiles(dispensary);
  const amplitude = useAmplitude();
  const Cart = useCart();
  const flags = useFlags();
  const dispensaryId = dispensary?.id;
  const { openModal } = useModals();
  const user = useUser();
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const delayedRender = useDelayedRender(PLACEHOLDER_DELAY_MS);

  const { redemptionType, hasTiers, isLoyaltyMember } = useLoyalty();
  const isLoggedIn = useObserver(() => user.isLoggedIn);
  const showBuyItAgainCarousel = useShowBuyItAgainCarousel();
  const showErnie = useErnie();

  // Check if we're on a referrals route
  const router = useRouter();
  const isReferralsRoute = router ? isReferralsRouteUtils(router) : false;

  useEffect(() => {
    if (isReferralsRoute && !user.loading && !isLoggedIn) {
      openModal(ModalNames.signUp);
    } else if (isReferralsRoute && isLoggedIn && isLoyaltyMember) {
      showErnie('Since you already have an account, the referral reward is not available.', ERNIE_TYPES.DANGER);
    }
  }, [isReferralsRoute, user.loading, isLoggedIn, isLoyaltyMember, openModal, showErnie]);

  // Show order success modals when viewOrderId is stored in session storage for embedded menus
  const isForceFullWidthCheckoutEnabled = flags['fintech.platform.force-full-width-checkout'] ?? false;
  const isLoyaltyWidgetEnabled = dispensary?.storeSettings?.showLoyaltyRewards ?? false;
  const isKioskProEnabled = useKioskPro();
  const adsEnabled = useAdsEnabled();

  const RemoteHero = useRemoteComponent(RemoteComponentSlot.Hero);
  const RemoteCategoryTiles = useRemoteComponent<React.ComponentProps<typeof CategoriesNav>>(
    RemoteComponentSlot.CategoryTiles,
    { fallback: CategoriesNav }
  );
  const RemoteLoyaltyWidget = useRemoteComponent<React.ComponentProps<typeof DutchieLoyaltyWidget>>(
    RemoteComponentSlot.LoyaltyWidget,
    { fallback: DutchieLoyaltyWidget }
  );
  const RemoteLoginWidget = useRemoteComponent<React.ComponentProps<typeof LoginCta>>(RemoteComponentSlot.LoginWidget, {
    fallback: LoginCta,
  });

  // don't show anonymous checkout for medical menus/orders ENG-52870
  const useAnonymousKioskCheckout =
    dispensary?.orderTypesConfigV2.kiosk?.anonymousCheckout && Cart.menuType === `rec` && !isKioskProEnabled;

  const productsForHomePage = useProductsForHomePage({ dispensaryId });

  const sponsoredBrandCarouselEnabled =
    flags['growth.ads.home-page-sponsored-brand-carousel.config']?.[UI.variant] ?? false;
  const showHomePageSponsoredBrandCarousel = adsEnabled && sponsoredBrandCarouselEnabled;

  useEffect(() => {
    amplitude.log(AmplitudeEvents.menu.viewHomepage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bannerIsBlank = useMemo(
    () => !dispensary?.menuBannerHtml || isBlank(getHtmlPlainText(dispensary?.menuBannerHtml)),
    [dispensary?.menuBannerHtml]
  );

  const showMenuBanner =
    !bannerIsBlank && UI.isVariant([`store-front`, `embedded`, ...(useAnonymousKioskCheckout ? [`kiosk`] : [])]);

  // Effect to handle page load event
  useEffect(() => {
    function handleLoad(): void {
      setIsPageLoaded(true);
      window.removeEventListener('load', handleLoad);
    }

    setIsPageLoaded(document.readyState === 'complete');

    if (document.readyState !== 'complete') {
      window.addEventListener('load', handleLoad);
    }
  }, []);

  // Effect to handle modal opening after page load
  useEffect(() => {
    if (
      !isForceFullWidthCheckoutEnabled ||
      !UI.isEmbedded ||
      !dispensary?.embedSettings?.enableFullScreenEmbeddedCheckout ||
      !isPageLoaded
    ) {
      return;
    }

    const storedOrderId = getPersistedValue('viewOrderId');
    const stashedOrder = getPersistedValue('stashedOrder');

    if (storedOrderId) {
      if (!user.hasExternalUser && stashedOrder) {
        // For guest users, show guest checkout success modal with order data
        try {
          Cart.stashedOrder = JSON.parse(stashedOrder);
          UI.viewingGuestOrderSuccess = true;
        } catch (error) {
          // If JSON parsing fails, log error and continue without showing success modal
          console.error('Failed to parse stashed order:', error);
          UI.viewingGuestOrderSuccess = false;
        }
      } else {
        // For logged in users, show order status modal
        user.viewOrderId = storedOrderId;
        void openModal(ModalNames.orderStatus);
      }
    }
  }, [
    user,
    openModal,
    Cart,
    UI,
    dispensary?.embedSettings?.enableFullScreenEmbeddedCheckout,
    isForceFullWidthCheckoutEnabled,
    isPageLoaded,
  ]);

  if (!dispensary) {
    return delayedRender(() => (
      <MainContent>
        <PlaceholderMenuImageBanner />
        <PlaceholderCategoriesNav />
      </MainContent>
    ));
  }

  return (
    <MainContent>
      <div data-testid='storefront-hero'>
        <RemoteHero />
      </div>

      {!!dispensary?.imageBanners?.length && !UI.isMobileEcommApp && (
        <MenuImageBanner banners={removeTypename(dispensary.imageBanners)} />
      )}

      {showMenuBanner && <MenuBanner htmlContent={dispensary.menuBannerHtml} color={dispensary.menuBannerColor} />}

      {!hideCategoryTiles && (
        <div data-testid='homepage-categories'>
          <RemoteCategoryTiles categories={categories} />
        </div>
      )}

      {isLoyaltyWidgetEnabled && (
        <RemoteLoyaltyWidget
          hasTiers={hasTiers}
          redemptionType={redemptionType ?? ''}
          isWidgetLink={!isKioskProEnabled}
          displayCTA
        />
      )}

      <RemoteLoginWidget margin='20px auto 40px' hideIfNeitherEnabled />

      {showHomePageSponsoredBrandCarousel && <HomePageSponsoredBrandCarousel />}

      {showBuyItAgainCarousel && <BuyItAgainCarousel />}

      {(!UI.isKiosk || isKioskProEnabled) && <PersonalizedProducts {...productsForHomePage} />}

      <HomepageCarouselSections />
    </MainContent>
  );
}
